import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { AssignedShippersPageResponse } from 'src/apis/monolith/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  name?: string;
  label: string;
  placeholder: string;
};

export const shipperOptionSchema = z.object({
  shipperRefId: z.coerce.number(),
  ...formOptionSchema.shape,
});

type ShipperOption = z.input<typeof shipperOptionSchema>;

export const ShippersSearchableMultiSelect: FC<Props> = ({
  label,
  placeholder,
  name = 'shipperEic',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<AssignedShippersPageResponse>({
        ...baseConfig,
        params: {
          sortColumn: 'shipperName',
          start: 0,
          pageSize: 100000,
          sortAscending: true,
        },
        url: `${PRISMA_CONFIG.monolithApiUrl}/tso/assigned-shippers/page`,
      }),
    { neededOnPageLoad: false }
  );

  const shippers = searchRequest.response?.data.data.map(
    (shipper) =>
      ({
        value: shipper.eic,
        label: shipper.shipperName,
        shipperRefId: shipper.shipperRefId,
      }) satisfies ShipperOption
  );

  const search = useClientSearch(
    shippers,
    (shipper, matcher) =>
      shipper.label.toLowerCase().match(matcher) ||
      shipper.value.toLowerCase().match(matcher)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.value}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>EIC:</strong> {option.value}
        </>
      )}
    />
  );
};
